import React, { useId, useRef } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import OverflowText from './OverflowText';
import { SIDENAV_WIDTH_VAR } from './NotifyProvider';

const SIDENAV_EDGE = `var(${SIDENAV_WIDTH_VAR}, 0px)`;

/**
 * ContextSelectPanel — full-height panel attached to the sidebar's right edge
 * for picking the current record (ticket #295: Select bid; #290: projects).
 * The page behind it is dimmed, the sidebar is not. Esc or the backdrop closes
 * it and focus returns to whatever opened it.
 *
 * Props:
 *   open               {bool}
 *   onClose            {func}
 *   title              {node}   "Select bid"
 *   search             {string} controlled search text
 *   onSearchChange     {func}   (text) => void
 *   searchPlaceholder  {string}
 *   searchAriaLabel    {string} defaults to the placeholder
 *   footer             {node}   pinned actions (left + right, space-between)
 *   children           {node}   ContextSelectSection / ContextSelectRow list
 */
const ContextSelectPanel = ({
  open,
  onClose,
  title,
  search = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  searchAriaLabel,
  footer,
  children,
}) => {
  const { ft } = useTheme().palette;
  const titleId = useId();
  const searchRef = useRef(null);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      transitionDuration={ft.motion.panel}
      SlideProps={{ onEntered: () => searchRef.current?.focus() }}
      BackdropProps={{
        sx: { left: SIDENAV_EDGE, backgroundColor: ft.overlay.backdrop },
      }}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': titleId,
        sx: {
          width: 400,
          maxWidth: `calc(100vw - ${SIDENAV_EDGE})`,
          top: 0,
          bottom: 0,
          left: SIDENAV_EDGE,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: ft.neutrals.white,
          boxShadow: ft.overlay.panelShadow,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 2,
          pr: 2,
          pb: 1.5,
          pl: 3,
        }}
      >
        <Typography
          id={titleId}
          variant="dialogTitle"
          sx={{ m: 0, color: ft.neutrals.textPrimary }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ width: 36, height: 36, color: ft.neutrals.textSecondary }}
        >
          <CloseOutlined sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pb: 1.5 }}>
        <TextField
          inputRef={searchRef}
          autoFocus
          fullWidth
          size="small"
          type="search"
          value={search}
          onChange={(event) => onSearchChange?.(event.target.value)}
          placeholder={searchPlaceholder}
          inputProps={{ 'aria-label': searchAriaLabel || searchPlaceholder }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined
                  sx={{ fontSize: 18, color: ft.neutrals.textSecondary }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider sx={{ borderColor: ft.neutrals.divider }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
        {children}
      </Box>

      {footer && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${ft.neutrals.divider}`,
            '& .MuiButton-root': { textTransform: 'none', fontWeight: 500 },
          }}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
};

/**
 * ContextSelectSection — uppercase section label inside the panel
 * ("Open bids", "Recently closed").
 */
export const ContextSelectSection = ({ children }) => {
  const { ft } = useTheme().palette;
  return (
    <Typography
      variant="sectionLabel"
      component="div"
      sx={{
        display: 'block',
        color: ft.neutrals.textSecondary,
        px: 3,
        pt: 1.5,
        pb: 0.75,
        // The first section sits closer to the divider than later ones.
        '&:first-child': { pt: 0.75 },
      }}
    >
      {children}
    </Typography>
  );
};

/**
 * ContextSelectRow — one pickable record: primary line (14px / 500,
 * ellipsis + tooltip when cut off), secondary line (12px gray, tabular),
 * optional right slot (status pill).
 *
 * Props:
 *   primary    {node}
 *   secondary  {node}
 *   end        {node}  right slot
 *   selected   {bool}  the current record (primary tint)
 *   onClick    {func}
 */
export const ContextSelectRow = ({
  primary,
  secondary,
  end,
  selected = false,
  onClick,
}) => {
  const { ft } = useTheme().palette;
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{
        gap: 1.5,
        px: 3,
        py: 1.25,
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: ft.primary.tint,
        },
        '&.Mui-focusVisible': { backgroundColor: ft.neutrals.tile },
        '&.Mui-selected.Mui-focusVisible': { backgroundColor: ft.primary.tint },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <OverflowText
          component="span"
          sx={{
            fontSize: 14,
            fontWeight: ft.fontWeights.medium,
            color: ft.neutrals.textPrimary,
          }}
        >
          {primary}
        </OverflowText>
        {secondary && (
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              color: ft.neutrals.textSecondary,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {secondary}
          </Typography>
        )}
      </Box>
      {end && <Box sx={{ flex: 'none', display: 'flex' }}>{end}</Box>}
    </ListItemButton>
  );
};

export default ContextSelectPanel;
